import { Injectable } from '@angular/core';
import { Event as RouterEvent, NavigationStart } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { CurrencyValue } from '@mmlogic/components';
import { Tenant } from '../model/tenants/tenant.model';
import { isAdminRole, isViewerRole, UserProfile } from '../model/tenants/user-profile.model';
import { TenantClientService } from './tenant-client.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private currentTenant: Tenant | null = null;
  private currentUserProfile: UserProfile | null = null;

  public tenantSubject = new BehaviorSubject<Tenant | null>(null);
  public userProfileSubject = new BehaviorSubject<UserProfile | null>(null);

  constructor(private tenantService: TenantClientService) {}

  private eventSource: EventSource | null = null;

  onNavigate(event: RouterEvent) {
    if (event instanceof NavigationStart) {
      this.setPath(event.url);
    }
  }

  setPath(url: string) {
    let tenant: string | null;
    const pathParts = url.substring(1).split('?')[0].split('/');

    if (
      pathParts &&
      pathParts.length > 1 &&
      ['data', 'model', 'apidocs', 'system'].indexOf(pathParts[0]) >= 0
    ) {
      tenant = pathParts[1];
    } else {
      tenant = pathParts[0];
    }

    if (tenant && tenant != 'new' && tenant != 'signup') {
      if (!this.currentTenant || this.currentTenant.tenantCode != tenant) {
        this.tenantService.getTenant(tenant).subscribe((tenant) => {
          this.currentTenant = tenant;
          this.tenantSubject.next(tenant);
        });
        this.tenantService.getUserProfile(tenant).subscribe((userProfile) => {
          this.currentUserProfile = userProfile;
          this.userProfileSubject.next(userProfile);
        });

        if (this.eventSource) {
          this.eventSource.close();
          this.eventSource = null;
        }

        this.tenantService
          .getEventSource(tenant)
          .subscribe((eventSource) => {
            this.eventSource = eventSource;
          });
      }
    } else if (this.currentTenant) {
      this.currentTenant = null;
      this.tenantSubject.next(null);
    }
  }

  hasCurrentTenant(): boolean {
    return !!this.currentTenant;
  }

  isAdmin(): boolean {
    return isAdminRole(this.currentUserProfile?.role);
  }

  isViewer(): boolean {
    return isViewerRole(this.currentUserProfile?.role);
  }

  /** Whether the current tenant has any access control configured, read once per
   *  session from the tenant fetched on selection (TASK-0321/TASK-0323). Defaults to
   *  true — an absent field (older API) or no tenant yet must keep asking capability
   *  checks, since only an explicit `false` licenses skipping them. */
  isAccessControlEnabled(): boolean {
    return this.currentTenant?.accessControlEnabled !== false;
  }

  getCurrentTenant(): BehaviorSubject<Tenant | null> {
    return this.tenantSubject;
  }

  getUpdates(): Observable<any> {
    return new Observable((observer) => {
      if (this.eventSource) {
        this.eventSource.onmessage = (event) => {
          const parsed = JSON.parse(event.data);
          if (parsed.type === 'KEEP_ALIVE') {
            return;
          }
          if (parsed.type === 'MODEL_RELOAD') {
            window.location.reload();
            return;
          }
          if (parsed.type === 'REBUILD_COMPLETE') {
            if (confirm('De rebuild is voltooid. Wil je de pagina opnieuw laden?')) {
              window.location.reload();
            }
            return;
          }
          observer.next(parsed);
        }
      }
    })
  }

  getLocale(): string {
    return this.getProfileLocale() ?? this.getBrowserLocale();
  }

  /**
   * The locale explicitly set on the user profile, or null when it is unset or
   * not a usable IETF language tag — the field is free text on the backend, so
   * an unusable value must not break formatting.
   */
  getProfileLocale(): string | null {
    const locale = this.currentUserProfile?.locale?.trim();
    if (!locale) {
      return null;
    }

    try {
      return Intl.getCanonicalLocales(locale)[0] ?? null;
    } catch {
      return null;
    }
  }

  getLanguage(): string {
    const raw = this.currentUserProfile?.language
             || this.currentUserProfile?.locale
             || this.getBrowserLocale();
    return raw.substring(0, 2).toLowerCase();
  }

  getBrowserLocale(): string {
    let locale = navigator.language;

    if ((!locale || locale.length < 5) && navigator.languages) {
      // try to find a more specific locale for the language found
      const specificLocale = navigator.languages.find(
        (l) => l.length == 5 && (!locale || l.startsWith(locale))
      );
      if (specificLocale) {
        locale = specificLocale;
      }
    }

    if (!locale) {
      locale = 'nl-NL';
    }

    return locale;
  }

  numberToString(value: number): string {
    return new Intl.NumberFormat(this.getLocale()).format(value);
  }

  percentageToString(value: number): string {
    return this.numberToString(value * 100) + ' %';
  }

  currencyToString(value: CurrencyValue): string {
    const formatted = new Intl.NumberFormat(this.getLocale(), {
      minimumFractionDigits: value.decimals ?? 2,
      maximumFractionDigits: value.decimals ?? 2,
    }).format(value.amount ?? 0);
    return formatted + ' ' + value.currency;
  }

  dateToString(value: string) {
    const date = Date.parse(value);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    return new Intl.DateTimeFormat(this.getLocale(), options).format(date);
  }

  dateTimeToString(value: string) {
    const date = Date.parse(value);
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    return new Intl.DateTimeFormat(this.getLocale(), options).format(date);
  }

  timeToString(value: string) {
    const date = Date.parse('1970-01-01T' + value);
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Intl.DateTimeFormat(this.getLocale(), options).format(date);
  }
}
